import { getRegistryURL } from "./Registry";
import { buildGetOptions, fetchWithErrorHandling, buildDeleteOptions, buildPostOptions } from "../FetchUtils";
import { useAuthUser } from "react-auth-kit";
import { getBaseURL, isRunningOnProd } from "../../utils/Environment";
import { getUserInfo } from "../aws/CognitoService";
import { useContext } from "react";
import { AppContext } from "utils/AppContext";
import type { IEnvironment, EnvType } from "../../types/EnvironmentTypes";

export const envTypes: EnvType[] = isRunningOnProd()
  ? [
      {
        name: "Prod",
        values: [8, 9],
      },
    ]
  : [
      {
        name: "Test",
        values: [1, 2],
      },
      {
        name: "Dev",
        values: [3, 4, 5, 6, 7],
      },
      {
        name: "Prod",
        values: [8, 9],
      },
    ];

const fixRegistryURL = (env: IEnvironment) => {
  if (env) {
    try {
      if (isScalableEnvironment(env.stage)) {
        env.registryURL = env.baseURL.replaceAll("https://", "https://registry.");
      } else {
        env.registryURL = getRegistryURL();
      }
    } catch (e) {
      console.error("Could not fix registry URL for env:", env, e);
    }
  }
};

export const getUserEmail = async (accessToken: string) => {
  if (accessToken) {
    const fetchedUserInfo = await getUserInfo(accessToken);
    return fetchedUserInfo.email;
  }
  return undefined;
};

export const listEnvironments = async (accessToken: string) => {
  const res = await fetchWithErrorHandling(getRegistryURL() + "/v1/registry/environment/list", buildGetOptions(accessToken));
  for (const env of res.list) {
    fixRegistryURL(env);
  }
  return res;
};

export const addEnvironment = async (
  accessToken: string,
  name: string,
  baseURL: string,
  stage: number,
  ttl?: number,
  clientBranch?: string,
) => {
  const ownerEmail = await getUserEmail(accessToken);
  return fetchWithErrorHandling(
    getRegistryURL() + "/v1/registry/environment/add",
    buildPostOptions(accessToken, { name, baseURL, stage, ttl, clientBranch, ownerEmail }),
  );
};

export const createEnvironment = async (
  accessToken: string,
  name: string,
  stage: number,
  ttl?: number,
  clientBranch?: string,
) => {
  const ownerEmail = await getUserEmail(accessToken);
  return fetchWithErrorHandling(
    getRegistryURL() + "/v1/registry/environment/create",
    buildPostOptions(accessToken, { name, stage, ttl, clientBranch, ownerEmail }),
  );
};

export const terminateEnvironment = async (accessToken: string, name: string) => {
  return fetchWithErrorHandling(
    getRegistryURL() + "/v1/registry/environment/terminate/" + name,
    buildDeleteOptions(accessToken),
  );
};

export const destroyEnvironment = async (accessToken: string, name: string) => {
  return fetchWithErrorHandling(
    getRegistryURL() + "/v1/registry/environment/destroy/" + name,
    buildDeleteOptions(accessToken),
  );
};

export const editEnvironment = async (
  accessToken: string,
  name: string,
  baseURL: string,
  stage: number,
  ttl?: number,
  clientBranch?: string,
  ownerEmail?: string,
) => {
  return fetchWithErrorHandling(
    getRegistryURL() + "/v1/registry/environment/edit",
    buildPostOptions(accessToken, { name, baseURL, stage, ttl, clientBranch, ownerEmail }),
  );
};

export const setDefaultEnvironment = async (accessToken: string, name: string) => {
  return fetchWithErrorHandling(
    getRegistryURL() + "/v1/registry/environment/set-default",
    buildPostOptions(accessToken, { name }),
  );
};

interface EvalEnvironmentStateCacheItem {
  time: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  resData: any;
}

const evalEnvironmentStateCache = new Map<string, EvalEnvironmentStateCacheItem>();

export const evalEnvironmentState = async (accessToken: string, name: string) => {
  const now = Date.now();
  let item = evalEnvironmentStateCache.get(name);
  if (item && now < item.time + 5000) {
    return item.resData;
  } else {
    if (item) {
      item.time = now;
    } else {
      item = {
        time: now,
        resData: {
          name: name,
          creationDate: 0,
          baseURL: "N/A",
          stage: 3,
          backendImageId: "N/A",
          backendImageName: "N/A",
          state: "unknown",
          privateIpAddress: "N/A",
          publicIpAddress: "N/A",
          activePackageId: "N/A",
          activeSchemaBranch: "N/A",
          activeSchemaVersion: 0,
          serverImageBranch: "N/A",
          gameServerCount: 0,
        },
      };
    }
    evalEnvironmentStateCache.set(name, item);
    try {
      item.resData = await fetchWithErrorHandling(
        getRegistryURL() + "/v1/registry/environment/eval-state/" + name,
        buildGetOptions(accessToken),
      );
      evalEnvironmentStateCache.set(name, item);
    } catch (e) {
      console.error(`Could not eval the state of env "${name}".`, e);
    }
    fixRegistryURL(item.resData);
    return item.resData;
  }
};

export const deployToDevEnvironment = async (
  accessToken: string,
  environmentName: string,
  backendImageId: string,
  backendFlavor: string,
) => {
  return fetchWithErrorHandling(
    getRegistryURL() + "/v1/registry/environment/deploy",
    buildPostOptions(accessToken, { environmentName, backendImageId, backendFlavor }),
  );
};

export const deployToDevRegistry = async (
  accessToken: string,
  baseURL: string,
  backendImageId: string,
  backendFlavor: string,
) => {
  return fetchWithErrorHandling(
    baseURL + "v1/registry/environment/deploy",
    buildPostOptions(accessToken, { environmentName: "registry", backendImageId, backendFlavor }),
  );
};

export const overrideFeature = async (
  accessToken: string,
  environmentName: string,
  feature: string,
  value: unknown,
  description?: string,
) => {
  return fetchWithErrorHandling(
    getRegistryURL() + "/v1/registry/environment/override-feature",
    buildPostOptions(accessToken, { environmentName, feature, value, description }),
  );
};

export const overrideFeatures = async (
  accessToken: string,
  environmentName: string,
  features: Record<string, unknown>,
  description?: string,
) => {
  return fetchWithErrorHandling(
    getRegistryURL() + "/v1/registry/environment/override-features",
    buildPostOptions(accessToken, { environmentName, features, description }),
  );
};

export const getAllFeatures = async (accessToken: string, environmentName: string) => {
  return fetchWithErrorHandling(
    getRegistryURL() + "/v1/registry/environment/all-features/" + environmentName,
    buildGetOptions(accessToken),
  );
};

export const shutdownEnvironment = async (accessToken: string, name: string) => {
  return fetchWithErrorHandling(
    getRegistryURL() + "/v1/registry/environment/shutdown",
    buildPostOptions(accessToken, { name }),
  );
};

export const replicateToDevEnvironment = async (
  accessToken: string,
  sourceEnvironmentName: string,
  targetEnvironmentName: string,
  features: string[],
  autoPublishDraftStaticData: boolean,
  skipDeployment: boolean,
) => {
  return fetchWithErrorHandling(
    getRegistryURL() + "/v1/registry/environment/replicate",
    buildPostOptions(accessToken, {
      sourceEnvironmentName,
      targetEnvironmentName,
      features,
      autoPublishDraftStaticData,
      skipDeployment,
    }),
  );
};

export const replicateToDevRegistry = async (
  accessToken: string,
  baseURL: string,
  sourceEnvironmentName: string,
  targetEnvironmentName: string,
  features: string[],
  autoPublishDraftStaticData: boolean,
  skipDeployment: boolean,
) => {
  return fetchWithErrorHandling(
    baseURL + "v1/registry/environment/replicate",
    buildPostOptions(accessToken, {
      sourceEnvironmentName,
      targetEnvironmentName,
      features,
      autoPublishDraftStaticData,
      skipDeployment,
    }),
  );
};

export const restartEnvironment = async (accessToken: string, environmentName: string) => {
  return fetchWithErrorHandling(
    getRegistryURL() + "/v1/registry/environment/restart",
    buildPostOptions(accessToken, { environmentName }),
  );
};

export const startEnvironment = async (accessToken: string, environmentName: string) => {
  return fetchWithErrorHandling(
    getRegistryURL() + "/v1/registry/environment/start",
    buildPostOptions(accessToken, { environmentName }),
  );
};

export const stopEnvironment = async (accessToken: string, environmentName: string) => {
  return fetchWithErrorHandling(
    getRegistryURL() + "/v1/registry/environment/stop",
    buildPostOptions(accessToken, { environmentName }),
  );
};

export const upgradeEnvironment = async (accessToken: string, environmentName: string, instanceType?: string) => {
  return fetchWithErrorHandling(
    getRegistryURL() + "/v1/registry/environment/upgrade",
    buildPostOptions(accessToken, { environmentName, instanceType }),
  );
};

export const fetchEnvironmentMetrics = async (accessToken: string, name: string) => {
  return fetchWithErrorHandling(getRegistryURL() + "/v1/registry/environment/metrics/" + name, buildGetOptions(accessToken));
};

export const cleanupDiskEnvironment = async (accessToken: string, name: string) => {
  return fetchWithErrorHandling(
    getRegistryURL() + "/v1/registry/environment/cleanup-disk",
    buildPostOptions(accessToken, { name }),
  );
};

export const softRestartEnvironment = async (accessToken: string, name: string) => {
  return fetchWithErrorHandling(
    getRegistryURL() + "/v1/registry/environment/soft-restart",
    buildPostOptions(accessToken, { name }),
  );
};

export const autoStopConf = async (accessToken: string, name: string) => {
  return fetchWithErrorHandling(
    getRegistryURL() + "/v1/registry/environment/auto-stop-conf/" + name,
    buildGetOptions(accessToken),
  );
};

export const useEnvironmentService = () => {
  const authUser = useAuthUser();
  const accessToken = authUser()?.access_token;
  const appContext = useContext(AppContext);

  return {
    listEnvironments: () => listEnvironments(accessToken),
    addEnvironment: (name: string, baseURL: string, stage: number, ttl?: number, clientBranch?: string) =>
      addEnvironment(accessToken, name, baseURL, stage, ttl, clientBranch),
    createDevEnvironment: (name: string, stage: number, ttl?: number, clientBranch?: string) =>
      createEnvironment(accessToken, name, stage, ttl, clientBranch),
    terminateEnvironment: (name: string) => terminateEnvironment(accessToken, name),
    destroyDevEnvironment: (name: string) => destroyEnvironment(accessToken, name),
    editEnvironment: (
      name: string,
      baseURL: string,
      stage: number,
      ttl?: number,
      clientBranch?: string,
      ownerEmail?: string,
    ) => editEnvironment(accessToken, name, baseURL, stage, ttl, clientBranch, ownerEmail),
    setDefaultEnvironment: (name: string) => setDefaultEnvironment(accessToken, name),
    evalEnvironmentState: (name: string) => evalEnvironmentState(accessToken, name),
    deployToDevEnvironment: (environmentName: string, backendImageId: string, backendFlavor: string) =>
      deployToDevEnvironment(accessToken, environmentName, backendImageId, backendFlavor),
    deployToDevRegistry: (backendImageId: string, backendFlavor: string) =>
      deployToDevRegistry(accessToken, getBaseURL(appContext), backendImageId, backendFlavor),
    overrideFeature: (environmentName: string, feature: string, value: unknown, description?: string) =>
      overrideFeature(accessToken, environmentName, feature, value, description),
    overrideFeatures: (environmentName: string, features: Record<string, unknown>, description?: string) =>
      overrideFeatures(accessToken, environmentName, features, description),
    getAllFeatures: (environmentName: string) => getAllFeatures(accessToken, environmentName),
    shutdownEnvironment: (name: string) => shutdownEnvironment(accessToken, name),
    replicateToDevEnvironment: (
      sourceEnvironmentName: string,
      targetEnvironmentName: string,
      features: string[],
      autoPublishDraftStaticData: boolean,
      skipDeployment: boolean,
    ) =>
      replicateToDevEnvironment(
        accessToken,
        sourceEnvironmentName,
        targetEnvironmentName,
        features,
        autoPublishDraftStaticData,
        skipDeployment,
      ),
    replicateToDevRegistry: (
      sourceEnvironmentName: string,
      targetEnvironmentName: string,
      features: string[],
      autoPublishDraftStaticData: boolean,
      skipDeployment: boolean,
    ) =>
      replicateToDevRegistry(
        accessToken,
        getBaseURL(appContext),
        sourceEnvironmentName,
        targetEnvironmentName,
        features,
        autoPublishDraftStaticData,
        skipDeployment,
      ),
    restartEnvironment: (environmentName: string) => restartEnvironment(accessToken, environmentName),
    upgradeEnvironment: (environmentName: string, instanceType?: string) =>
      upgradeEnvironment(accessToken, environmentName, instanceType),
    startEnvironment: (environmentName: string) => startEnvironment(accessToken, environmentName),
    stopEnvironment: (environmentName: string) => stopEnvironment(accessToken, environmentName),
    fetchEnvironmentMetrics: (name: string) => fetchEnvironmentMetrics(accessToken, name),
    cleanupDiskEnvironment: (name: string) => cleanupDiskEnvironment(accessToken, name),
    softRestartEnvironment: (name: string) => softRestartEnvironment(accessToken, name),
    autoStopConf: (name: string) => autoStopConf(accessToken, name),
  };
};

export const getEnvironmentStageData = (stage: number) => {
  switch (stage) {
    case 1:
      return ["#800080", "#ff9fff", "TEST", "#fe81ff"];
    case 2:
      return ["#460080", "#d099ff", "TEST", "#c581ff"];
    case 3:
      return ["#004080", "#a0ceff", "DEV", "#0184ff"];
    case 4:
      return ["#007580", "#93f4ff", "DEV", "#81f5ff"];
    case 5:
      return ["#00801a", "#9bffb1", "QA", "#81ff99"];
    case 6:
      return ["#648000", "#e8ffa1", "QA", "#e3ff81"];
    case 7:
      return ["#806b00", "#ffed9b", "STABLE", "#ffe981"];
    case 8:
      return ["#804900", "#ffd49f", "STAGING", "#ffc581"];
    case 9:
      return ["#80001e", "#ff98af", "PROD", "#ff819f"];
    default:
      return ["#000000", "#ff0000", "INVALID", "#c0c0c0"];
  }
};

export const isStagingEnvironment = (stage: number) => stage === 8;
export const isProdEnvironment = (stage: number) => stage === 9;
export const isScalableEnvironment = (stage: number) => isStagingEnvironment(stage) || isProdEnvironment(stage);
